import { useQuery } from '@tanstack/react-query';

import { Badge, Card, EmptyState, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/formatters';

interface NotifRow {
  id: string;
  channel: 'EMAIL' | 'SMS';
  template: string;
  status: string;
  error: string | null;
  createdAt: string;
  sentAt: string | null;
  member?: { firstName: string; lastName: string; email: string };
}

export function NotificationsPage() {
  const { data: stats } = useQuery({
    queryKey: ['notifStats'],
    queryFn: async () => (await api.get('/admin/notifications/stats')).data,
  });
  const { data: list, isLoading } = useQuery<{ items: NotifRow[] }>({
    queryKey: ['notifs'],
    queryFn: async () => (await api.get('/admin/notifications', { params: { pageSize: 100 } })).data,
  });

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl">Notifications</h1>

      {stats?.last30d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Email delivered" value={stats.last30d.byChannel.EMAIL.sent} />
          <Stat label="SMS delivered" value={stats.last30d.byChannel.SMS.sent} />
          <Stat label="SMS used / cap" value={`${stats.last30d.smsUsage.used} / ${stats.last30d.smsUsage.cap}`} />
          <Stat label="Email delivery" value={`${Math.round(stats.last30d.byChannel.EMAIL.deliveryRate * 100)}%`} />
        </div>
      )}

      {isLoading ? (
        <Spinner />
      ) : (list?.items ?? []).length === 0 ? (
        <EmptyState title="No notifications yet" />
      ) : (
        <Card>
          <ul className="divide-y divide-cream-300">
            {list!.items.map((n) => (
              <li key={n.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium">{n.template}</div>
                  <div className="text-sm text-ink-muted">
                    {n.channel} · {formatDateTime(n.sentAt ?? n.createdAt)}
                    {n.member && ` · ${n.member.firstName} ${n.member.lastName}`}
                  </div>
                  {n.error && <div className="text-xs text-lacquer mt-1">{n.error}</div>}
                </div>
                <Badge variant={n.status === 'SENT' ? 'success' : n.status === 'FAILED' ? 'error' : 'default'}>
                  {n.status}
                </Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Card className="px-5 py-4">
      <div className="text-sm text-ink-muted">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </Card>
  );
}
